import { canonical, FOOD_RULES, INGREDIENT_FLAGS, } from './vocab.js';
/**
 * Matrix eetregel → vlaggen. Bewust eenvoudig en uitlegbaar:
 * - halal: geen varken, geen alcohol (ook niet in de saus), geen gelatine (meestal van varken);
 *   vlees zelf mag (het gezin koopt halal vlees; dat zien we niet aan een ingrediëntnaam);
 * - geen varkensvlees: geen varken; gelatine is "let op";
 * - geen rundvlees (veel hindoeïstische gezinnen): geen rund; gelatine is "let op";
 * - vegetarisch: geen vlees, vis, schaal- en schelpdieren, gelatine;
 * - veganistisch: vegetarisch + geen zuivel, ei, honing;
 * - koosjer: geen varken, geen schaal- en schelpdieren (ook paling en inktvis), geen gelatine, en
 *   geen vlees samen met zuivel in één gerecht (zie ruleConflicts);
 * - geen alcohol: geen alcohol.
 */
export const RULE_MATRIX = {
    halal: { conflict: ['pork', 'alcohol', 'gelatin'], check: [] },
    no_pork: { conflict: ['pork'], check: ['gelatin'] },
    no_beef: { conflict: ['beef'], check: ['gelatin'] },
    vegetarian: { conflict: ['meat', 'fish', 'shellfish', 'gelatin'], check: [] },
    vegan: {
        conflict: ['meat', 'fish', 'shellfish', 'gelatin', 'dairy', 'egg', 'honey'],
        check: [],
    },
    kosher: { conflict: ['pork', 'shellfish', 'gelatin'], check: [] },
    no_alcohol: { conflict: ['alcohol'], check: [] },
};
/** Botsingen van gevonden vlaggen met een set eetregels (per regel, in woordenlijstvolgorde). */
export function ruleConflicts(found, rules) {
    const out = [];
    for (const rule of canonical(rules, FOOD_RULES)) {
        const m = RULE_MATRIX[rule];
        const sure = found.flags.filter((f) => m.conflict.indexOf(f) >= 0);
        // Koosjer: vlees en zuivel samen in één gerecht.
        if (rule === 'kosher' &&
            found.flags.indexOf('meat') >= 0 &&
            found.flags.indexOf('dairy') >= 0) {
            sure.push('meat', 'dairy');
        }
        if (sure.length > 0) {
            out.push({ rule, flags: canonical(sure, INGREDIENT_FLAGS), severity: 'conflict' });
            continue;
        }
        const soft = [
            ...found.maybe.filter((f) => m.conflict.indexOf(f) >= 0 || m.check.indexOf(f) >= 0),
            ...found.flags.filter((f) => m.check.indexOf(f) >= 0),
        ];
        if (soft.length > 0) {
            out.push({ rule, flags: canonical(soft, INGREDIENT_FLAGS), severity: 'check' });
        }
    }
    return out;
}
/**
 * Voor wie is dit gerecht lastig? Eén regel per lid met een botsing; eerst de zekere botsingen,
 * daarna de "let op"-gevallen, verder in de volgorde van `people` (gezinsvolgorde). Leeg = past
 * bij iedereen. Voor het huishouden: geef alle leden die meeëten ("Wie eet mee?", standaard
 * iedereen). `eatsOther`: leden die bij dit recept "iets anders eten" (profile_eats_other, 0023):
 * die tellen niet mee, zodat de waarschuwing niet bij elk gerecht terugkomt.
 */
export function foodConflicts(found, people, eatsOther = []) {
    const hits = [];
    for (const person of people) {
        if (eatsOther.indexOf(person.memberId) >= 0)
            continue;
        const rc = ruleConflicts(found, person.foodRules);
        if (rc.length === 0)
            continue;
        const hard = rc.some((c) => c.severity === 'conflict');
        const flags = [];
        for (const c of rc)
            flags.push(...c.flags);
        hits.push({
            memberId: person.memberId,
            name: person.name,
            severity: hard ? 'conflict' : 'check',
            rules: rc.map((c) => c.rule),
            flags: canonical(flags, INGREDIENT_FLAGS),
        });
    }
    const rank = (c) => (c.severity === 'conflict' ? 0 : 1);
    return hits
        .map((c, i) => ({ c, i }))
        .sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i)
        .map((x) => x.c);
}
/**
 * Samenvatting voor één waarschuwing: "Yakub eet geen varkensvlees", "Anne en Yakub ...",
 * "Jullie eten halal". `presentCount` = aantal mensen aan tafel. null als er niets botst.
 */
export function summarizeConflicts(conflicts, presentCount) {
    if (conflicts.length === 0)
        return null;
    const byRule = [];
    for (const rule of FOOD_RULES) {
        const who = conflicts.filter((c) => c.rules.indexOf(rule) >= 0);
        if (who.length === 0)
            continue;
        byRule.push({ rule, memberIds: who.map((c) => c.memberId), names: who.map((c) => c.name) });
    }
    return {
        severity: conflicts.some((c) => c.severity === 'conflict') ? 'conflict' : 'check',
        everyone: presentCount > 0 && conflicts.length >= presentCount,
        byRule,
    };
}
/**
 * Knoppen bij een eetwaarschuwing (docs/DESIGN.md §8.P.6). Raakt het iedereen aan tafel, dan is
 * ruilen de primaire knop (als er een ruil is). Raakt het maar een paar mensen, dan is
 * "Toevoegen" primair (de rest eet het gewoon), met "{naam} eet iets anders" (onthoudt een
 * uitzondering voor dit recept) en ruilen als tweede keuze: niet betuttelen.
 */
export function warningActions(summary, hasSwap) {
    if (summary.everyone) {
        return hasSwap
            ? { primary: 'swap', secondary: ['add_anyway'] }
            : { primary: 'add_anyway', secondary: [] };
    }
    return {
        primary: 'add_anyway',
        secondary: hasSwap ? ['eats_other', 'swap'] : ['eats_other'],
    };
}
/**
 * Pictogram bij een gerecht in de weekmenu-dag: alleen als het iedereen raakt, of als iemand
 * geraakt wordt die (nog) geen "eet iets anders" heeft. De uitzonderingen zijn al verwerkt in
 * `conflicts` (foodConflicts met eatsOther); zo zeurt het weekmenu niet bij elk vleesgerecht.
 */
export function showMenuMarker(conflicts) {
    return conflicts.some((c) => c.severity === 'conflict');
}
//# sourceMappingURL=conflicts.js.map